import React from 'react';
import {
  LISTABLEDATE,
  TIME,
  DOW
} from './../../../../data/constants/components';
import { KINDOFBID } from './../../../../data/constants/index';

// Remove static language import - will use dynamic labels passed as parameters
export function getComponent(type, parameter) {

  function isSelectField() {
    return (
      <DOW
        parameter={parameter}
        handleFormChange={this.handleFormChange}
        handleTargetparameter={this.handleTargetparameter}
      />
    );
  }
  /**
   * components for bid options
   * [QUAN = SelectField] | @params {props}
   * [LISTABLEDATE = DatePicker] | @params {props}
   * [TIME = TimePicker] | @params {props}
   * [DOW = SelectField] | @params {props}
   * [LAYSTATION = SelectField] | @params {props}
   * @type {Object}
   */
  const components = {
    QUAN: isSelectField.call(this),
    LISTABLEDATE: (
      <LISTABLEDATE
        parameter={parameter}
        handleFormChange={this.handleFormChange}
        handleTargetparameter={this.handleTargetparameter}
        kindOfBid={this.props.bidPreferenceClasses.classOptionAttributes.name}
        disabled={this.state.disabledSecondDate}
        minDate={this.state.secondDate}
        secondDate={this.state.secondDateUpdated}
        classOptionAttributes={this.props.bidPreferenceClasses.classOptionAttributes}
        allDateValues={this.state.allDateValues || {}}
        onDateError={this.handleDateError}
        selectedParameterValues={this.state.selectedParameterValues}
        preferenceOptionId={this.props.bidPreferenceClasses.classOptionAttributes.preferenceOptionId}
        pairingdate={this.props.pairingdate}
        pairingName={this.state.pairingName}
        disableDialogSaveButton={this.disableDialogSaveButton}
        setSelectedParameterValues={this.setSelectedParameterValues}
      />
    ),
    TIME: (
      <TIME
        parameter={parameter}
        handleFormChange={this.handleFormChange}
        handleTargetparameter={this.handleTargetparameter}
      />
    ),
    DOW: isSelectField.call(this),
    LAYSTATION: isSelectField.call(this),
    ANYSTATION: isSelectField.call(this)
  };
  return components[type];
}

export function getActionBidMessage(type, ADDING_BID = {}) {
  const message = {
    save: ADDING_BID.ADD_MESSAGE || 'Request added successfully',
    edit: ADDING_BID.EDIT_MESSAGE || 'Request updated successfully',
    delete: ADDING_BID.DELETE_MESSAGE || 'Request deleted successfully',
    deleteAllError: ADDING_BID.DELETE_ALL_FAILED_MESSAGE || 'Failed to delete all requests',
    default: ''
  };
  return message[type] || message.default;
}

export function getParameterRelation(
  selectedRelation,
  parameterValidRelations
) {
  if (parameterValidRelations.includes(selectedRelation)) {
    return selectedRelation;
  }
  return parameterValidRelations[0];
}

export function getDefaultParameterValue(parameters, parameterId) {
  let value;
  parameters.forEach(item => {
    if (item.parameterId === parameterId) {
      value = item.defaultValue;
    }
  });
  return value;
}

export function getParameterValue(
  selectedParameterValues,
  parameters,
  parameterId,
  isHiddenParameter
) {
  let value;
  selectedParameterValues.forEach(item => {
    if (item && item.parameterId && item.parameterId === parameterId) {
      value = item.value;
    }
  });
  if (value === undefined || isHiddenParameter) {
    value = getDefaultParameterValue(parameters, parameterId);
  }
  return value;
}

export function getParameterIndexWithRelations(parameters = []) {
  return parameters.findIndex(parameter => {
    if (parameter.validRelations.length > 1) {
      return true;
    }
    return false;
  });
}

export function limitToOneBidPerGroup(preferences, newBid) {
  if (!Array.isArray(preferences) || typeof newBid !== 'object' || !newBid.properties) return false;

  const newBidHasSingletonProperty = newBid.properties.some(prop => prop.propertyId === KINDOFBID.LIMIT_ONE_BID_PER_GROUP);
  if (!newBidHasSingletonProperty) return false; // no restriction if new bid isn't singleton

  // Check if the preferences already has a singleton bid
  const result = preferences.some(bid => bid.preferenceOptionId === newBid.preferenceOptionId);
  return result;
}

// Validation configuration for different bid types
const BID_VALIDATION_CONFIG = {
  // Bids that should never disable the add button
  NO_VALIDATION: {
    ids: KINDOFBID.NOT_DISABLE_ADD_BID_TO_COMBINED,
    validator: () => false
  },
  
  // Single parameter validation (first element only)
  SINGLE_PARAM: {
    ids: [681, ...KINDOFBID.PAIRING_ATTRIBUTES_NOT_DISABLE],
    validator: (arr) => {
      if (arr.length === 0) return true;
      const firstParam = arr[0];
      return !firstParam || !firstParam.value || firstParam.value === undefined;
    }
  },
  
  // Pairing with dates validation - special handling for when dates get reset
  PAIRING_WITH_DATES: {
    ids: [
      { id: KINDOFBID.PAIRING_START_ON_DATE, requiredParams: 2 },
      { id: KINDOFBID.SPECIFIC_PAIRING_ON_DATES, requiredParams: 3 },
      { id: KINDOFBID.SPECIFIC_PAIRING_STARTING_ON_DATES, requiredParams: 3 }
    ],
    validator: (arr, config) => {
      if (arr.length === 0) return true;
      
      // First parameter (pairing) must have a valid value
      const pairingParam = arr[0];
      if (!pairingParam || !pairingParam.value || pairingParam.value === undefined || pairingParam.value === null) {
        return true; // Disable button - pairing is required
      }
      
      // For arrays of length 9 (with DESIRE/AVOID input), only check required date parameters
      if (arr.length === 9) {
        // Check only the required date parameters (not the null padding or DESIRE/AVOID)
        for (let i = 1; i < config.requiredParams; i++) {
          const dateParam = arr[i];
          if (!dateParam || dateParam.value === null || dateParam.value === undefined || dateParam.value === '') {
            return true; // Disable button - required date parameter is empty
          }
        }
        
        // Check DESIRE/AVOID input at index 8 if it exists
        const desireAvoidParam = arr[8];
        if (desireAvoidParam && desireAvoidParam.parameterId === '') {
          const value = desireAvoidParam.value;
          if (value === undefined || value === null || value === '') {
            return true; // Disable button - DESIRE/AVOID input is empty
          }
        }
        
        return false; // Enable button - all required parameters are valid
      }
      
      // For standard arrays, check all date parameters (index 1 and beyond)
      for (let i = 1; i < arr.length; i++) {
        const dateParam = arr[i];
        if (!dateParam || dateParam.value === null || dateParam.value === undefined || dateParam.value === '') {
          return true; // Disable button - date parameter is empty
        }
      }
      
      return false; // Enable button - all parameters have valid values
    }
  }
};

/**
 * Helper function to check if a parameter value is empty
 * @param {*} value - The value to check
 * @param {boolean} allowEmptyString - Whether to treat empty strings as valid
 * @returns {boolean} - True if the value is considered empty
 */
function isEmptyValue(value, allowEmptyString = false) {
  if (value === null || value === undefined) return true;
  if (allowEmptyString && typeof value === 'string' && value.trim() === '') return true;
  return false;
}

/**
 * Generic function to validate if array has empty values based on bid type
 * @param {Array} arr - Array of parameter values to validate
 * @param {number} preferenceOptionId - The preference option ID to determine validation rules
 * @param {Object} dateErrors - Optional object containing date validation errors
 * @returns {boolean} - True if validation should disable the add button
 */
export function hasEmptyValue(arr, preferenceOptionId, dateErrors = {}) {
  
  // Check if there are any date validation errors
  if (dateErrors && Object.keys(dateErrors).length > 0) {
    const hasActiveErrors = Object.values(dateErrors).some(error => error === true);
    if (hasActiveErrors) {
      return true; // Disable button if there are date validation errors
    }
  }
  
  // Check for no validation required bids
  if (BID_VALIDATION_CONFIG.NO_VALIDATION.ids.includes(preferenceOptionId)) {
    return BID_VALIDATION_CONFIG.NO_VALIDATION.validator();
  }
  
  // Check for single parameter validation
  if (BID_VALIDATION_CONFIG.SINGLE_PARAM.ids.includes(preferenceOptionId)) {
    const result = BID_VALIDATION_CONFIG.SINGLE_PARAM.validator(arr);
    return result;
  }
  
  // Check for pairing with dates validation (special handling)
  const pairingConfig = BID_VALIDATION_CONFIG.PAIRING_WITH_DATES.ids.find(config => config.id === preferenceOptionId);
  if (pairingConfig) {
    const result = BID_VALIDATION_CONFIG.PAIRING_WITH_DATES.validator(arr, pairingConfig);
    return result;
  }
  
  // Default validation for other bid types
  if (arr.length === 1 && arr[0] === null) {
    return true;
  }
  
  const result = arr.some(item => {
    if (item === null) return true;
    if (typeof item === 'object' && item !== null) {
      return isEmptyValue(item.value);
    }
    return false;
  });
  
  return result;
}

// can be used to deep clone and unfreeze an object
export function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

export function deepFreeze(obj) {
  Object.freeze(obj);

  Object.getOwnPropertyNames(obj).forEach(prop => {
    const value = obj[prop];
    if (
      value !== null &&
      (typeof value === 'object' || typeof value === 'function') &&
      !Object.isFrozen(value)
    ) {
      deepFreeze(value);
    }
  });

  return obj;
}

export function validateDifferentValue(copy, newValues, handleisAddButtonDisabled) {
  if (!copy || !newValues) {
    return;
  }

  function toBase64Url(obj) {
    return btoa(JSON.stringify(obj))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  const originalParams = copy || {};
  const currentParams = newValues || {};

  const origToken = toBase64Url(originalParams);
  const currToken = toBase64Url(currentParams);

  let isAddButtonDisabled = origToken === currToken ? true : false;
  handleisAddButtonDisabled(isAddButtonDisabled);
}

/**
 * Determines if a bid requires date validation based on its properties and parameters
 * @param {Object} classOptionAttributes - The bid's class option attributes from API
 * @returns {boolean} - true if bid requires date validation, false otherwise
 */
export function requiresDateValidation(classOptionAttributes) {

  if (!classOptionAttributes || !classOptionAttributes.properties || !classOptionAttributes.parameters) {
    return false;
  }
  
  const bidId = classOptionAttributes.preferenceOptionId;  
  // Check if the bid has the "Reversible" property (propertyId: 103)
  // If it has this property, it should be ignored from date validation
  const hasReversibleProperty = classOptionAttributes.properties.some(
    property => property.propertyId === 103
  );
    
  if (hasReversibleProperty) {
    return false;
  }
  
  // Count date parameters (attributeId: 14)
  const dateParameters = classOptionAttributes.parameters.filter(param => 
    param.attribute && param.attribute.attributeId === 14
  );
  
  const dateParameterCount = dateParameters.length;  
  // Only apply date validation to bids with MULTIPLE date fields (2 or more)
  const requiresValidation = dateParameterCount >= 2 && !hasReversibleProperty;
  return requiresValidation;
}

/**
 * Gets date parameter sequences for a bid based on its parameters
 * @param {Object} classOptionAttributes - The bid's class option attributes from API
 * @returns {Array} - Array of parameter sequences that are date fields
 */
export function getDateParameterSequences(classOptionAttributes) {
  if (!classOptionAttributes || !classOptionAttributes.parameters) {
    return [];
  }
  
  const dateSequences = classOptionAttributes.parameters
    .filter(param => param.attribute && param.attribute.attributeId === 14)
    .map(param => param.sequence)
    .sort((a, b) => a - b);
  
  return dateSequences;
}